import { useEffect, useEffectEvent } from 'react';
import { fetchFlow } from '@/lib/api/email-flows';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib';
import { separateTags } from '@/features/email-flows/logic/audienceBuilder.logic';
import { useBroadcastEditorStore } from '../stores';
import { getBroadcastStatusText } from '../logic/broadcast.logic';
import { useStanQuery } from '@/queries';
import type { Tag } from '@/features/email-flows/types/audience.types';
import { useBroadcastTemplateVariables } from '../queries/useBroadcastQueries';

interface UseBroadcastEditorDataProps {
    flowId: number;
}

/**
 * Hook that fetches broadcast editor data and populates the store.
 * This should be called once at the page level.
 */
export function useBroadcastEditorData({ flowId }: UseBroadcastEditorDataProps) {
    const setFlowData = useBroadcastEditorStore(state => state.setFlowData);
    const setTemplateData = useBroadcastEditorStore(state => state.setTemplateData);
    const setSelectedTags = useBroadcastEditorStore(state => state.setSelectedTags);
    const setSelectedExcludedTags = useBroadcastEditorStore(state => state.setSelectedExcludedTags);
    const setMetadata = useBroadcastEditorStore(state => state.setMetadata);
    const generatedDraft = useBroadcastEditorStore(state => state.generatedDraft);

    // Fetch broadcast data if editing
    const { data: flowData, isLoading } = useStanQuery({
        queryKey: [QUERY_KEYS.FLOW_DETAILS, flowId],
        queryFn: () => fetchFlow(flowId),
        enabled: flowId > 0,
        staleTime: 0,
        meta: {
            error: {
                sentryMessage: '[EMAIL_FLOWS] Failed to fetch broadcast details',
                title: 'Failed to load broadcast',
                message: 'Unable to load broadcast details. Please try again.',
                feature: SENTRY_FEATURES.EMAIL_FLOWS,
            },
        },
    });

    const handleFlowDataUpdate = useEffectEvent((data: typeof flowData) => {
        if (data) {
            setFlowData(data);

            // Initialize tags from broadcast data
            if (data.data?.specific_time?.tags) {
                const { includeTags, excludeTags } = separateTags(data.data.specific_time.tags);
                setSelectedTags(includeTags);
                setSelectedExcludedTags(excludeTags);
            }

            // Set metadata
            const broadcastStatus = getBroadcastStatusText(data);
            setMetadata({ broadcastStatus });
        }
    });

    // Update store when flow data changes
    useEffect(() => {
        handleFlowDataUpdate(flowData);
    }, [flowData]);

    // Fetch template variables using query hook
    const { data: templateData } = useBroadcastTemplateVariables();

    // Event handler that always uses latest store setter
    const handleTemplateDataUpdate = useEffectEvent(
        (template: typeof templateData, flow: typeof flowData, draft: typeof generatedDraft) => {
            if (!template) return;

            const templateEmail = template.flow_email_template;
            const emailData = flow?.flow_emails?.[0];

            // Get tags from flowData if available
            let includeTags: Tag[] = [];
            let excludeTags: Tag[] = [];
            if (flow?.data?.specific_time?.tags) {
                const separated = separateTags(flow.data.specific_time.tags);
                includeTags = separated.includeTags;
                excludeTags = separated.excludeTags;
            }

            // Priority: saved email data > generated draft > template defaults
            const defaultSubject = emailData?.data?.subject || draft?.subject || templateEmail.default.subject || '';
            const defaultBody = emailData?.data?.body || draft?.body || templateEmail.default.body || '';

            setTemplateData(
                {
                    subject: templateEmail.variables.subject || [],
                    body: templateEmail.variables.body || [],
                },
                {
                    subject: templateEmail.default.subject || '',
                    body: templateEmail.default.body || '',
                    tags: [],
                    excludedTags: [],
                },
                {
                    subject: defaultSubject,
                    body: defaultBody,
                    tags: includeTags,
                    excludedTags: excludeTags,
                }
            );
        }
    );

    // Update store when template data or flow data changes
    useEffect(() => {
        handleTemplateDataUpdate(templateData, flowData, generatedDraft);
    }, [templateData, flowData, generatedDraft]);

    return {
        flowData,
        isLoading,
    };
}
